/**
 * Purpose: Self-contained CSS for the copilot widget, injected once per
 *          page at first mount. Keeps the widget hermetic — host SPAs
 *          do not need to import a stylesheet, configure Tailwind to
 *          scan node_modules, or coordinate design tokens.
 *
 * Implementation notes:
 *   - Class names are prefixed `ada-copilot-` to avoid colliding with
 *     anything in the host. Theming goes through public CSS custom
 *     properties (`--ada-copilot-*`) that hosts set on any ancestor; the
 *     rules only read private `--_*` aliases declared on
 *     `.ada-copilot-root` as `var(--ada-copilot-x, <default>)`. Never
 *     assign a public `--ada-copilot-*` name inside this stylesheet — a
 *     declaration on the root would shadow the host's inherited value.
 *   - `box-sizing: border-box` applies to every element in the widget.
 *   - The injection is idempotent — guarded by a `data-ada-copilot`
 *     attribute on the `<style>` element so multiple Copilot mounts in
 *     the same page (or React StrictMode double-mounts) only add one.
 *   - All sizes are in `rem` so the widget scales with the host's root
 *     font size; positions are `vh`-ish via `fixed` + `bottom`/`right`
 *     so the floating button anchors to the viewport.
 *
 * Created: 2026-05-08 17:00
 *
 * Changelog:
 *   - 2026-05-08 17:00 — Initial version.
 *   - 2026-05-09 19:00 — Slice 2 of P5 streaming: add `.ada-copilot-cursor`
 *     — a blinking caret rendered at the end of an in-flight assistant
 *     bubble while chunks stream in.
 *   - 2026-05-14 14:00 — Add layout variants. The component now applies
 *     `ada-copilot-root-<layout>` and `ada-copilot-panel-<layout>` classes
 *     so the same DOM can render as the original popup, as a docked left
 *     or right side pane, or inline inside a host-controlled container.
 *     The base `.ada-copilot-panel` rules still describe the popup look;
 *     the variant classes override `position`, sizing, radii and shadow.
 *   - 2026-05-14 15:30 — Accept an optional injection target (any
 *     `Document` or `ShadowRoot`-like node with `appendChild` and the
 *     ability to query a `<style data-ada-copilot>` element). Defaults
 *     to `document.head`, so the existing React npm consumers are
 *     unaffected. The CDN wrapper passes its element's `ShadowRoot` so
 *     widget styles are scoped to the shadow tree and never bleed into
 *     the host page.
 *   - 2026-05-15 17:00 — Add `.ada-copilot-iconbtn-on` modifier used by
 *     the new user-level voice toggles (mic + speaker) in the header.
 *     Renders the active state in the accent colour with a subtle
 *     accent background, so users can tell at a glance which voice
 *     features they have turned on.
 *   - 2026-05-16 10:30 — File-upload composer + model picker styles.
 *     `.ada-copilot-form` flips to a column flex so the new
 *     `.ada-copilot-attachments` chip strip can sit above the
 *     `.ada-copilot-input-row`. Adds `.ada-copilot-chip` (and its
 *     `-static` variant rendered inside message bubbles),
 *     `.ada-copilot-form-dragover` for the drag-and-drop affordance,
 *     `.ada-copilot-file-input` (hidden file picker), and the
 *     `.ada-copilot-modelpicker-*` set used by the type-as-you-filter
 *     model combobox that lives between the header and messages.
 *   - 2026-05-18 12:00 — Docked-mode resize + collapse styles. The
 *     `.ada-copilot-panel-docked-left|right` widths now read from
 *     `--ada-copilot-dock-current-width` (set inline by the component)
 *     with the previous `min(22rem, 100vw)` as the fallback, so existing
 *     hosts that never touch the new affordances are byte-identical.
 *     Adds `.ada-copilot-resize-handle` (a 6 px col-resize strip on the
 *     panel's free edge) and `.ada-copilot-rail` (the 2.25 rem-wide
 *     collapsed rail rendered in place of the panel with a single
 *     expand button). Two side-specific modifiers — `-left` / `-right`
 *     — pin both to the correct viewport edge.
 *   - 2026-06-01 14:10 — Bump `.ada-copilot-rail` z-index from 1 to
 *     2147483001 (matching the panel). At z-index:1 the collapsed rail
 *     sat below host chrome, so any host with a fixed top header (e.g.
 *     Prodity) painted over the rail's expand chevron, making a
 *     collapsed docked widget impossible to reopen. Also make the rail
 *     itself the clickable expand control (it is now a <button>): added
 *     button resets + a `:hover` colour, repointed the chevron-icon
 *     sizing from the removed `.ada-copilot-rail-btn` to `.ada-copilot-rail
 *     svg`. A click anywhere on the strip reopens the panel.
 *   - 2026-06-01 15:00 — Add `.ada-copilot-resize-corner`: a 16px top-left
 *     corner grip for resizing the popup panel (bottom-right anchored). The
 *     visible chevron is drawn with `::after` so the hit area stays a
 *     comfortable square; hover/active tint it with the accent colour.
 *   - 2026-06-01 15:30 — Replace the single corner grip with the full
 *     floating-window control set: eight `.ada-copilot-resize-edge-*`
 *     handles (4 edges + 4 corners, kept inside the panel since it clips),
 *     a `.ada-copilot-header-draggable` move cursor, a persistent grip on
 *     the SE corner, and `.ada-copilot-panel-gesturing` to suppress text
 *     selection mid drag/resize.
 *   - 2026-06-03 08:00 — Add `.ada-copilot-md`: styling for rendered
 *     assistant markdown (paragraphs, lists, headings, links, inline +
 *     block code, blockquotes, tables). Resets the bubble's `pre-wrap` to
 *     normal flow and collapses outer margins so bubble padding isn't
 *     doubled.
 *   - 2026-09-25 08:04 — UX overhaul (Celestina feedback). Theming fixed:
 *     tokens are now fallbacks behind private `--_*` aliases so host
 *     overrides actually apply (they were shadowed by the root's own
 *     declarations), plus new tokens (surface, accent-soft, bubble fg,
 *     header, danger, focus ring, font). `border-box` everywhere (the
 *     inline panel overflowed its box by 2px → horizontal scrollbar).
 *     New composer (one frame with text box + toolbar, labelled Dictate /
 *     Talk buttons that go icon-only in narrow panels via a container
 *     query, Send/Stop pill, Enter hint), voice-conversation bar, typing
 *     dots, tool-status spinner, suggestion chips, compact header,
 *     full-screen popup on phones, dvh heights, 16px input on touch
 *     devices (iOS zoom), and prefers-reduced-motion. Removed the unused
 *     `.ada-copilot-mic*` rules.
 *   - 2026-09-25 08:27 — Move `container-type` from the panel to the composer
 *     toolbar: on the panel it zeroed the inline widget's intrinsic width
 *     and collapsed it in content-sized slots (review finding).
 *   - 2026-10-06 17:30 — Add `.ada-copilot-voicebar-interrupt` (the
 *                        Interrupt button of the opt-in in-chat realtime
 *                        voice bar): same pill as End, accent-filled so it
 *                        reads as the primary action while the reply plays.
 */
const STYLE_ATTR = 'data-ada-copilot';
const CSS = `
/* --- Design tokens ----------------------------------------------------- *
 * Public theming API: hosts set --ada-copilot-* on any ancestor (for the
 * CDN element: on <ada-copilot> itself or above — custom properties
 * inherit through the shadow boundary). The widget only ever reads the
 * private --_* aliases below, each of which falls back to a default when
 * the public token is unset. Declaring the defaults as fallbacks (instead
 * of assigning the public names here) is what makes outside overrides
 * win: a declaration on .ada-copilot-root would shadow the inherited
 * host value. */
.ada-copilot-root {
  --_font: var(--ada-copilot-font-family, ui-sans-serif, system-ui, -apple-system, "Segoe UI",
    Roboto, "Helvetica Neue", Arial, sans-serif);
  --_font-size: var(--ada-copilot-font-size, 0.875rem);
  --_bg: var(--ada-copilot-bg, #ffffff);
  --_fg: var(--ada-copilot-fg, #0f172a);
  --_muted: var(--ada-copilot-muted, #64748b);
  --_border: var(--ada-copilot-border, #e2e8f0);
  --_surface: var(--ada-copilot-surface, #f1f5f9);
  --_accent: var(--ada-copilot-accent, #4f46e5);
  --_accent-fg: var(--ada-copilot-accent-fg, #ffffff);
  --_accent-soft: var(--ada-copilot-accent-soft, color-mix(in srgb, var(--_accent) 12%, var(--_bg)));
  --_bubble-user: var(--ada-copilot-bubble-user, var(--_accent));
  --_bubble-user-fg: var(--ada-copilot-bubble-user-fg, var(--_accent-fg));
  --_bubble-assistant: var(--ada-copilot-bubble-assistant, var(--_surface));
  --_bubble-assistant-fg: var(--ada-copilot-bubble-assistant-fg, var(--_fg));
  --_header-bg: var(--ada-copilot-header-bg, var(--_bg));
  --_header-fg: var(--ada-copilot-header-fg, var(--_fg));
  --_danger: var(--ada-copilot-danger, #b91c1c);
  --_danger-soft: var(--ada-copilot-danger-soft, #fef2f2);
  --_focus-ring: var(--ada-copilot-focus-ring, color-mix(in srgb, var(--_accent) 30%, transparent));
  --_shadow: var(--ada-copilot-shadow, 0 10px 30px rgba(15, 23, 42, 0.18));
  --_radius: var(--ada-copilot-radius, 0.75rem);
  font-family: var(--_font);
  color: var(--_fg);
  font-size: var(--_font-size);
  line-height: 1.45;
}
/* Borders and padding count inside declared sizes everywhere in the
 * widget. With content-box, the inline panel (width: 100% + 1px borders)
 * overflowed its box by 2px and produced a stray horizontal scrollbar. */
.ada-copilot-root,
.ada-copilot-root *,
.ada-copilot-root *::before,
.ada-copilot-root *::after {
  box-sizing: border-box;
}
.ada-copilot-root button:focus-visible,
.ada-copilot-root [role="separator"]:focus-visible {
  outline: 2px solid var(--_accent);
  outline-offset: 2px;
}
.ada-copilot-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.ada-copilot-launcher {
  position: fixed;
  right: 1.5rem;
  bottom: 1.5rem;
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 9999px;
  background: var(--_accent);
  color: var(--_accent-fg);
  border: none;
  cursor: pointer;
  box-shadow: var(--_shadow);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2147483000;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.ada-copilot-launcher:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.22);
}
.ada-copilot-launcher svg {
  width: 1.5rem;
  height: 1.5rem;
}
.ada-copilot-panel {
  position: fixed;
  right: 1.5rem;
  bottom: 5.5rem;
  width: min(22rem, calc(100vw - 3rem));
  height: min(32rem, calc(100vh - 8rem));
  height: min(32rem, calc(100dvh - 8rem));
  background: var(--_bg);
  border: 1px solid var(--_border);
  border-radius: var(--_radius);
  box-shadow: var(--_shadow);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 2147483001;
}
.ada-copilot-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem 0.625rem 1rem;
  border-bottom: 1px solid var(--_border);
  background: var(--_header-bg);
  color: var(--_header-fg);
  flex: 0 0 auto;
}
.ada-copilot-header-compact {
  justify-content: flex-end;
  padding: 0.25rem 0.5rem;
}
.ada-copilot-header-text {
  min-width: 0;
}
.ada-copilot-title {
  font-weight: 600;
  font-size: 0.9375rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ada-copilot-subtitle {
  font-size: 0.75rem;
  color: var(--_muted);
  margin-top: 0.125rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ada-copilot-header-actions {
  display: flex;
  align-items: center;
  gap: 0.125rem;
  flex: 0 0 auto;
}
.ada-copilot-iconbtn,
.ada-copilot-close {
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--_muted);
  width: 2rem;
  height: 2rem;
  padding: 0;
  border-radius: 0.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.ada-copilot-iconbtn:hover,
.ada-copilot-close:hover {
  color: var(--_fg);
  background: var(--_surface);
}
.ada-copilot-iconbtn-on,
.ada-copilot-iconbtn-on:hover {
  color: var(--_accent);
  background: var(--_accent-soft);
}
.ada-copilot-history-row {
  text-align: left;
  background: var(--_bg);
  border: 1px solid var(--_border);
  border-radius: 0.625rem;
  padding: 0.6rem 0.75rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  transition: background 120ms;
  font: inherit;
  color: inherit;
  flex: 0 0 auto;
}
.ada-copilot-history-row:hover {
  background: var(--_surface);
}
.ada-copilot-history-title {
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--_fg);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ada-copilot-history-meta {
  font-size: 0.7rem;
  color: var(--_muted);
}

/* --- Messages ---------------------------------------------------------- */
.ada-copilot-messages {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  background: var(--_bg);
}
.ada-copilot-empty {
  color: var(--_muted);
  font-size: 0.8125rem;
  text-align: center;
  margin: auto;
  max-width: 18rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}
.ada-copilot-empty-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 9999px;
  background: var(--_accent-soft);
  color: var(--_accent);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.ada-copilot-empty-icon svg {
  width: 1.25rem;
  height: 1.25rem;
}
.ada-copilot-suggestions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.375rem;
  width: 100%;
  margin-top: 0.25rem;
}
.ada-copilot-suggestions-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--_muted);
}
.ada-copilot-suggestion {
  text-align: left;
  background: var(--_bg);
  color: var(--_fg);
  border: 1px solid var(--_border);
  border-radius: 0.75rem;
  padding: 0.5rem 0.75rem;
  font: inherit;
  font-size: 0.8125rem;
  cursor: pointer;
  transition: background 120ms, border-color 120ms;
}
.ada-copilot-suggestion:hover {
  background: var(--_accent-soft);
  border-color: var(--_accent);
}
.ada-copilot-bubble {
  padding: 0.55rem 0.8rem;
  border-radius: 1rem;
  max-width: 85%;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  flex: 0 0 auto;
}
/* Rendered-markdown container inside an assistant bubble. The bubble
 * keeps white-space: pre-wrap for plain user text; markdown output is
 * block HTML, so reset to normal flow and collapse the outer margins so
 * the bubble padding isn't doubled. */
.ada-copilot-md {
  white-space: normal;
}
.ada-copilot-md > :first-child {
  margin-top: 0;
}
.ada-copilot-md > :last-child {
  margin-bottom: 0;
}
.ada-copilot-md p {
  margin: 0 0 0.5rem;
}
.ada-copilot-md ul,
.ada-copilot-md ol {
  margin: 0 0 0.5rem;
  padding-left: 1.25rem;
}
.ada-copilot-md li {
  margin: 0.125rem 0;
}
.ada-copilot-md h1,
.ada-copilot-md h2,
.ada-copilot-md h3,
.ada-copilot-md h4 {
  margin: 0.5rem 0 0.25rem;
  font-weight: 600;
  line-height: 1.25;
}
.ada-copilot-md h1 {
  font-size: 1.15em;
}
.ada-copilot-md h2 {
  font-size: 1.1em;
}
.ada-copilot-md h3,
.ada-copilot-md h4 {
  font-size: 1.05em;
}
.ada-copilot-md a {
  color: var(--_accent);
  text-decoration: underline;
}
.ada-copilot-md code {
  background: rgba(15, 23, 42, 0.06);
  padding: 0.1em 0.3em;
  border-radius: 0.25rem;
  font-size: 0.9em;
}
.ada-copilot-md pre {
  background: rgba(15, 23, 42, 0.06);
  padding: 0.5rem 0.625rem;
  border-radius: 0.375rem;
  overflow-x: auto;
  margin: 0 0 0.5rem;
}
.ada-copilot-md pre code {
  background: none;
  padding: 0;
  font-size: 0.85em;
}
.ada-copilot-md blockquote {
  margin: 0 0 0.5rem;
  padding-left: 0.625rem;
  border-left: 3px solid var(--_border);
  color: var(--_muted);
}
.ada-copilot-md table {
  display: block;
  max-width: 100%;
  overflow-x: auto;
  border-collapse: collapse;
  margin: 0 0 0.5rem;
  font-size: 0.95em;
}
.ada-copilot-md th,
.ada-copilot-md td {
  border: 1px solid var(--_border);
  padding: 0.25rem 0.5rem;
}
.ada-copilot-md img {
  max-width: 100%;
}
.ada-copilot-bubble-user {
  align-self: flex-end;
  background: var(--_bubble-user);
  color: var(--_bubble-user-fg);
  border-bottom-right-radius: 0.3rem;
}
.ada-copilot-bubble-assistant {
  align-self: flex-start;
  background: var(--_bubble-assistant);
  color: var(--_bubble-assistant-fg);
  border-bottom-left-radius: 0.3rem;
}
.ada-copilot-bubble-error {
  align-self: stretch;
  max-width: none;
  background: var(--_danger-soft);
  border: 1px solid color-mix(in srgb, var(--_danger) 25%, transparent);
  color: var(--_danger);
  border-radius: 0.625rem;
  font-size: 0.8125rem;
}
.ada-copilot-cursor {
  display: inline-block;
  margin-left: 0.15rem;
  color: var(--_muted);
  animation: ada-copilot-cursor-blink 1s steps(2, start) infinite;
}
@keyframes ada-copilot-cursor-blink {
  to { visibility: hidden; }
}
/* "Typing" indicator: three bouncing dots in an assistant-style bubble. */
.ada-copilot-typing {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.75rem 0.9rem;
  border-radius: 1rem;
  border-bottom-left-radius: 0.3rem;
  background: var(--_bubble-assistant);
  flex: 0 0 auto;
}
.ada-copilot-typing-dot {
  width: 0.4rem;
  height: 0.4rem;
  border-radius: 9999px;
  background: var(--_muted);
  animation: ada-copilot-bounce 1.2s infinite ease-in-out;
}
.ada-copilot-typing-dot:nth-child(2) {
  animation-delay: 0.15s;
}
.ada-copilot-typing-dot:nth-child(3) {
  animation-delay: 0.3s;
}
@keyframes ada-copilot-bounce {
  0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
  40% { transform: translateY(-3px); opacity: 1; }
}
/* One-line status (tool in progress, history loading). */
.ada-copilot-status {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--_muted);
  font-size: 0.8125rem;
  padding: 0.25rem 0.25rem;
  flex: 0 0 auto;
}
.ada-copilot-spinner {
  width: 0.875rem;
  height: 0.875rem;
  border-radius: 9999px;
  border: 2px solid var(--_border);
  border-top-color: var(--_accent);
  animation: ada-copilot-spin 0.8s linear infinite;
  flex: 0 0 auto;
}
@keyframes ada-copilot-spin {
  to { transform: rotate(360deg); }
}
.ada-copilot-notice {
  align-self: center;
  color: var(--_muted);
  font-size: 0.75rem;
  flex: 0 0 auto;
}

/* --- Composer ---------------------------------------------------------- */
.ada-copilot-form {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
  border-top: 1px solid var(--_border);
  background: var(--_bg);
  position: relative;
  flex: 0 0 auto;
}
.ada-copilot-form-dragover .ada-copilot-inputbox {
  outline: 2px dashed var(--_accent);
  outline-offset: 2px;
  background: var(--_accent-soft);
}
/* The text box and its toolbar share one rounded frame. */
.ada-copilot-inputbox {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--_border);
  border-radius: 0.875rem;
  background: var(--_bg);
  transition: border-color 120ms, box-shadow 120ms;
}
.ada-copilot-inputbox:focus-within {
  border-color: var(--_accent);
  box-shadow: 0 0 0 3px var(--_focus-ring);
}
.ada-copilot-input {
  display: block;
  width: 100%;
  border: none;
  background: transparent;
  padding: 0.625rem 0.75rem 0.25rem;
  font: inherit;
  color: var(--_fg);
  outline: none;
  resize: none;
  min-height: 2.5rem;
  max-height: 9rem;
  overflow-y: auto;
}
.ada-copilot-input::placeholder {
  color: var(--_muted);
}
.ada-copilot-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.375rem;
  padding: 0.25rem 0.375rem 0.375rem;
  /* Query container for the icon-only fallback below. Kept on the toolbar
   * (not the panel): size containment zeroes the element's intrinsic
   * width, which would collapse an inline widget in a content-sized slot. */
  container-type: inline-size;
  container-name: ada-copilot-toolbar;
}
.ada-copilot-toolbar-start {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
}
.ada-copilot-tool {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  height: 2rem;
  padding: 0 0.55rem;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 9999px;
  color: var(--_muted);
  font: inherit;
  font-size: 0.8125rem;
  cursor: pointer;
  white-space: nowrap;
}
.ada-copilot-tool:hover {
  color: var(--_fg);
  background: var(--_surface);
}
.ada-copilot-tool:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.ada-copilot-tool svg {
  width: 1.05rem;
  height: 1.05rem;
  flex: 0 0 auto;
}
.ada-copilot-tool-on,
.ada-copilot-tool-on:hover {
  background: var(--_danger-soft);
  border-color: color-mix(in srgb, var(--_danger) 35%, transparent);
  color: var(--_danger);
  animation: ada-copilot-mic-pulse 1.2s ease-in-out infinite;
}
.ada-copilot-tool-active,
.ada-copilot-tool-active:hover {
  background: var(--_accent-soft);
  border-color: var(--_accent);
  color: var(--_accent);
}
@keyframes ada-copilot-mic-pulse {
  0%, 100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--_danger) 35%, transparent); }
  50% { box-shadow: 0 0 0 5px transparent; }
}
.ada-copilot-send {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 2rem;
  padding: 0 0.85rem;
  background: var(--_accent);
  color: var(--_accent-fg);
  border: none;
  border-radius: 9999px;
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  flex: 0 0 auto;
  transition: opacity 0.15s ease;
}
.ada-copilot-send svg {
  width: 0.95rem;
  height: 0.95rem;
}
.ada-copilot-send:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.ada-copilot-stop {
  background: var(--_fg);
  color: var(--_bg);
}
.ada-copilot-hint {
  font-size: 0.6875rem;
  color: var(--_muted);
  text-align: center;
  margin-top: -0.125rem;
}
/* Voice-conversation status bar above the text box. */
.ada-copilot-voicebar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.375rem 0.375rem 0.75rem;
  border-radius: 0.75rem;
  background: var(--_accent-soft);
  color: var(--_fg);
  font-size: 0.8125rem;
}
.ada-copilot-voicebar-dot {
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 9999px;
  background: var(--_accent);
  flex: 0 0 auto;
  animation: ada-copilot-breathe 1.4s ease-in-out infinite;
}
.ada-copilot-voicebar-listening .ada-copilot-voicebar-dot {
  background: var(--_danger);
}
@keyframes ada-copilot-breathe {
  0%, 100% { transform: scale(0.8); opacity: 0.6; }
  50% { transform: scale(1.15); opacity: 1; }
}
.ada-copilot-voicebar-text {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ada-copilot-voicebar-text strong {
  font-weight: 600;
}
.ada-copilot-voicebar-end {
  background: var(--_bg);
  color: var(--_fg);
  border: 1px solid var(--_border);
  border-radius: 9999px;
  height: 1.75rem;
  padding: 0 0.75rem;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  flex: 0 0 auto;
}
.ada-copilot-voicebar-end:hover {
  background: var(--_surface);
}
.ada-copilot-voicebar-interrupt {
  background: var(--_accent);
  color: var(--_accent-fg);
  border: 1px solid var(--_accent);
  border-radius: 9999px;
  height: 1.75rem;
  padding: 0 0.75rem;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  flex: 0 0 auto;
}
.ada-copilot-voicebar-interrupt:hover {
  filter: brightness(1.08);
}
/* Narrow panel (toolbar ≈ panel width − 38px): keep the composer buttons
 * icon-only (the tooltip and aria-label still explain them). */
@container ada-copilot-toolbar (max-width: 320px) {
  .ada-copilot-tool-label {
    display: none;
  }
  .ada-copilot-tool {
    padding: 0 0.45rem;
  }
}
/* Touch devices: no hover tooltips or physical Enter key to explain, and
 * iOS Safari zooms the page when a focused field is under 16px. */
@media (pointer: coarse) {
  .ada-copilot-hint {
    display: none;
  }
  .ada-copilot-input {
    font-size: 16px;
  }
}
.ada-copilot-file-input {
  display: none;
}
.ada-copilot-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}
.ada-copilot-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.45rem;
  font-size: 0.75rem;
  background: var(--_surface);
  border: 1px solid var(--_border);
  border-radius: 999px;
  color: var(--_fg);
  max-width: 14rem;
}
.ada-copilot-chip-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 0 1 auto;
  min-width: 0;
}
.ada-copilot-chip-size {
  color: var(--_muted);
  font-variant-numeric: tabular-nums;
}
.ada-copilot-chip-remove {
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--_muted);
  padding: 0 0.15rem;
  font-size: 1rem;
  line-height: 1;
  border-radius: 9999px;
}
.ada-copilot-chip-remove:hover {
  color: var(--_fg);
  background: var(--_border);
}
.ada-copilot-chip-remove:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.ada-copilot-chip-static {
  background: rgba(255, 255, 255, 0.6);
  color: var(--_fg);
}
.ada-copilot-bubble-attachments {
  margin-top: 0.4rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}

/* --- Model picker ----------------------------------------------------- *
 * Surfaced between the header and the messages list when the host opts
 * in via enableModelPicker. The trigger is a single row showing the
 * active model; clicking it expands an absolutely-positioned panel
 * with a search input and a scrollable filtered list. Absolute
 * positioning lets the panel overlay the messages container without
 * shifting layout — and the parent .ada-copilot-modelpicker carries
 * position: relative so the panel anchors to it. */

.ada-copilot-modelpicker {
  position: relative;
  border-bottom: 1px solid var(--_border);
  background: var(--_bg);
  flex: 0 0 auto;
}
.ada-copilot-modelpicker-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.875rem;
  background: transparent;
  border: none;
  cursor: pointer;
  font: inherit;
  color: var(--_fg);
  text-align: left;
}
.ada-copilot-modelpicker-trigger:hover {
  background: var(--_surface);
}
.ada-copilot-modelpicker-trigger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.ada-copilot-modelpicker-label {
  color: var(--_muted);
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.ada-copilot-modelpicker-value {
  flex: 1 1 auto;
  font-size: 0.8125rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ada-copilot-modelpicker-panel {
  position: absolute;
  top: 100%;
  left: 0.5rem;
  right: 0.5rem;
  margin-top: 0.25rem;
  background: var(--_bg);
  border: 1px solid var(--_border);
  border-radius: 0.5rem;
  box-shadow: var(--_shadow);
  z-index: 1;
  display: flex;
  flex-direction: column;
  max-height: 18rem;
  overflow: hidden;
}
.ada-copilot-modelpicker-search {
  border: none;
  border-bottom: 1px solid var(--_border);
  padding: 0.5rem 0.75rem;
  font: inherit;
  font-size: 0.8125rem;
  outline: none;
  color: var(--_fg);
  background: var(--_bg);
}
.ada-copilot-modelpicker-search:focus {
  border-bottom-color: var(--_accent);
}
.ada-copilot-modelpicker-list {
  flex: 1 1 auto;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}
.ada-copilot-modelpicker-empty {
  padding: 0.75rem;
  color: var(--_muted);
  font-size: 0.8125rem;
  text-align: center;
}
.ada-copilot-modelpicker-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
  padding: 0.5rem 0.75rem;
  background: transparent;
  border: none;
  border-bottom: 1px solid var(--_border);
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: var(--_fg);
}
.ada-copilot-modelpicker-row:last-child {
  border-bottom: none;
}
.ada-copilot-modelpicker-row:hover {
  background: var(--_surface);
}
.ada-copilot-modelpicker-row-current {
  background: var(--_accent-soft);
}
.ada-copilot-modelpicker-row-key {
  font-size: 0.8125rem;
  font-weight: 500;
}
.ada-copilot-modelpicker-row-meta {
  font-size: 0.7rem;
  color: var(--_muted);
}

/* --- Layout variants -------------------------------------------------- *
 * The base .ada-copilot-panel rules above describe the floating "popup"
 * look. Each variant below overrides the geometry of the panel (and, for
 * 'inline', the root container) while keeping the inner chrome untouched.
 * Selectors share specificity with the base class and rely on source-
 * order to win — keep these blocks AFTER the base rules. */

.ada-copilot-root-inline {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
}
.ada-copilot-panel-inline {
  position: static;
  right: auto;
  bottom: auto;
  top: auto;
  left: auto;
  width: 100%;
  height: 100%;
  max-width: none;
  max-height: none;
  border-radius: 0;
  box-shadow: none;
  flex: 1 1 auto;
  min-height: 0;
}
.ada-copilot-panel-docked-left {
  right: auto;
  bottom: 0;
  top: 0;
  left: 0;
  width: var(--ada-copilot-dock-current-width, min(22rem, 100vw));
  max-width: none;
  height: 100vh;
  height: 100dvh;
  max-height: none;
  border-radius: 0;
  border-left: none;
  box-shadow: 4px 0 12px rgba(15, 23, 42, 0.08);
}
.ada-copilot-panel-docked-right {
  right: 0;
  bottom: 0;
  top: 0;
  left: auto;
  width: var(--ada-copilot-dock-current-width, min(22rem, 100vw));
  max-width: none;
  height: 100vh;
  height: 100dvh;
  max-height: none;
  border-radius: 0;
  border-right: none;
  box-shadow: -4px 0 12px rgba(15, 23, 42, 0.08);
}
/* Popup on a phone-sized viewport: take the whole screen. The component
 * adds this class (and stops applying the dragged/resized rect) below the
 * compact breakpoint. */
.ada-copilot-panel-fullscreen {
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: auto;
  max-width: none;
  max-height: none;
  border: none;
  border-radius: 0;
  box-shadow: none;
}

/* Drag-to-resize handle. Lives on the panel's "free" edge — the edge
 * facing the rest of the page (right edge of a left-docked panel; left
 * edge of a right-docked panel). 6 px wide so pointer targeting is
 * forgiving on touch, but visually almost invisible until hover. */
.ada-copilot-resize-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: col-resize;
  background: transparent;
  z-index: 2;
  touch-action: none;
  user-select: none;
}
.ada-copilot-resize-handle-left {
  /* Sits on the RIGHT edge of a left-docked panel. */
  right: -3px;
}
.ada-copilot-resize-handle-right {
  /* Sits on the LEFT edge of a right-docked panel. */
  left: -3px;
}
.ada-copilot-resize-handle:hover,
.ada-copilot-resize-handle-active {
  background: var(--_accent);
  opacity: 0.25;
}

/* Popup-mode floating-window resize handles. Eight transparent hit zones
 * pinned inside the panel edges/corners (kept inside because the panel has
 * overflow:hidden, which would clip anything placed outside). Corners
 * are larger and sit above the edges so they win at the overlaps. The
 * header above carries the move gesture. */
.ada-copilot-resize-edge {
  position: absolute;
  z-index: 3;
  touch-action: none;
  user-select: none;
}
/* Edges — a 6px band along each side, inset by 14px so the corners own the
 * extremities. */
.ada-copilot-resize-edge-n {
  top: 0;
  left: 14px;
  right: 14px;
  height: 6px;
  cursor: ns-resize;
}
.ada-copilot-resize-edge-s {
  bottom: 0;
  left: 14px;
  right: 14px;
  height: 6px;
  cursor: ns-resize;
}
.ada-copilot-resize-edge-w {
  left: 0;
  top: 14px;
  bottom: 14px;
  width: 6px;
  cursor: ew-resize;
}
.ada-copilot-resize-edge-e {
  right: 0;
  top: 14px;
  bottom: 14px;
  width: 6px;
  cursor: ew-resize;
}
/* Corners — 14px squares with diagonal cursors. */
.ada-copilot-resize-edge-nw,
.ada-copilot-resize-edge-ne,
.ada-copilot-resize-edge-sw,
.ada-copilot-resize-edge-se {
  width: 14px;
  height: 14px;
  z-index: 4;
}
.ada-copilot-resize-edge-nw {
  top: 0;
  left: 0;
  cursor: nwse-resize;
}
.ada-copilot-resize-edge-se {
  bottom: 0;
  right: 0;
  cursor: nwse-resize;
}
.ada-copilot-resize-edge-ne {
  top: 0;
  right: 0;
  cursor: nesw-resize;
}
.ada-copilot-resize-edge-sw {
  bottom: 0;
  left: 0;
  cursor: nesw-resize;
}
/* A subtle persistent grip in the bottom-right corner as the universal
 * "resizable" hint; the other handles are cursor-only. */
.ada-copilot-resize-edge-se::after {
  content: '';
  position: absolute;
  right: 3px;
  bottom: 3px;
  width: 6px;
  height: 6px;
  border-right: 2px solid var(--_muted);
  border-bottom: 2px solid var(--_muted);
  border-bottom-right-radius: 3px;
  opacity: 0.5;
}
.ada-copilot-resize-edge-se:hover::after {
  border-color: var(--_accent);
  opacity: 1;
}
/* Header is the move grip in popup mode. Buttons inside keep their own
 * pointer cursor; the drag handler ignores clicks that land on them. */
.ada-copilot-header-draggable {
  cursor: move;
  touch-action: none;
}
/* While a move/resize gesture is in flight, kill text selection across the
 * whole panel so dragging never highlights chat content. */
.ada-copilot-panel-gesturing,
.ada-copilot-panel-gesturing * {
  user-select: none;
}

/* Collapsed rail. Rendered INSTEAD of the panel when the user collapses
 * a docked widget — a thin always-visible strip with one expand button
 * so the user can bring the panel back. Anchored to the same edge the
 * panel was docked against. */
.ada-copilot-rail {
  position: fixed;
  top: 0;
  bottom: 0;
  width: 2.25rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 0.75rem;
  /* The rail is a full-height <button> so a click anywhere on the strip
   * reopens the panel; these reset the native button chrome. */
  appearance: none;
  cursor: pointer;
  color: var(--_muted);
  background: var(--_bg);
  border: 1px solid var(--_border);
  /* Match the panel/launcher stacking (2147483001 / 2147483000). The
   * rail previously used z-index:1, which let any host page with a
   * fixed header paint over the rail's expand chevron — making a
   * collapsed docked widget impossible to reopen. */
  z-index: 2147483001;
}
.ada-copilot-rail-left {
  left: 0;
  border-left: none;
  border-top: none;
  border-bottom: none;
  box-shadow: 4px 0 12px rgba(15, 23, 42, 0.08);
}
.ada-copilot-rail-right {
  right: 0;
  border-right: none;
  border-top: none;
  border-bottom: none;
  box-shadow: -4px 0 12px rgba(15, 23, 42, 0.08);
}
.ada-copilot-rail:hover {
  color: var(--_fg);
}
.ada-copilot-rail svg {
  width: 1.1rem;
  height: 1.1rem;
  display: block;
}

@media (prefers-reduced-motion: reduce) {
  .ada-copilot-root *,
  .ada-copilot-root *::before,
  .ada-copilot-root *::after {
    animation-duration: 0.001ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.001ms !important;
  }
}
`;
// Track which targets have already received the stylesheet. A
// module-level boolean would block legitimate second injections when
// a host mounts multiple shadow roots; per-target tracking lets every
// shadow root self-style exactly once.
const INJECTED = new WeakSet();
export function ensureStylesInjected(target) {
    if (typeof document === 'undefined')
        return;
    const node = target ?? document.head;
    if (!node)
        return;
    if (INJECTED.has(node))
        return;
    if (node.querySelector(`style[${STYLE_ATTR}]`)) {
        INJECTED.add(node);
        return;
    }
    const style = document.createElement('style');
    style.setAttribute(STYLE_ATTR, '');
    style.textContent = CSS;
    node.appendChild(style);
    INJECTED.add(node);
}
//# sourceMappingURL=styles.js.map